import { useEffect } from 'react';
import type { ReactNode } from 'react';
import type { ExpenseCategory, ExpenseStatus } from '@/lib/types';
import { CATEGORY_LABELS, STATUS_LABELS } from '@/lib/types';
import { categoryColor, toDesignStatus } from '@/lib/designAdapters';
import { Icons } from '@/components/icons';

/* Small presentational components ported from the Cafe Receipts design
   (components.jsx), retyped against the domain model. */

export function StatusBadge({ status }: { status: ExpenseStatus }) {
  return (
    <span className={`badge ${toDesignStatus(status)}`}>
      <span className="dot" />
      {STATUS_LABELS[status]}
    </span>
  );
}

export function CategoryChip({ category }: { category: ExpenseCategory }) {
  return (
    <span className="chip" style={{ color: categoryColor(category) }}>
      <span className="sw" />
      <span style={{ color: 'var(--ink-2)' }}>{CATEGORY_LABELS[category]}</span>
    </span>
  );
}

export function StatusSwitcher({
  value,
  onChange,
}: {
  value: ExpenseStatus;
  onChange: (status: ExpenseStatus) => void;
}) {
  const opts: { id: ExpenseStatus; label: string }[] = [
    { id: 'pending', label: 'Pending' },
    { id: 'reimbursed', label: 'Reimbursed' },
    { id: 'donation', label: 'Donated' },
  ];
  return (
    <div className="status-switch">
      {opts.map((o) => (
        <button
          key={o.id}
          className={value === o.id ? `on ${toDesignStatus(o.id)}` : ''}
          onClick={() => onChange(o.id)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function Modal({
  open,
  title,
  onClose,
  children,
  headerExtras,
}: {
  open: boolean;
  title: ReactNode;
  onClose?: () => void;
  children: ReactNode;
  headerExtras?: ReactNode;
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="modal-bg" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-h">
          <h2>{title}</h2>
          <div className="spacer" />
          {headerExtras}
          <button className="btn icon ghost" onClick={onClose} title="Close (Esc)">
            <Icons.X size={16} />
          </button>
        </div>
        <div className="modal-b">{children}</div>
      </div>
    </div>
  );
}
